import React, { useState } from 'react';
import {
  Typography,
  TextField,
  Box,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import type { Exercise, CreateVariationInput } from '../services/types';
import { ExerciseService } from '../services/exerciseService';

export interface ExerciseVariationsPanelProps {
  open: boolean;
  exercise: Exercise | null;
  variations: Exercise[];
  loading: boolean;
  onClose: () => void;
  onVariationsChange: (variations: Exercise[]) => void;
  onEditVariation: (variation: Exercise) => void;
  onError: (msg: string) => void;
  onRefreshExercises: () => void;
}

export const ExerciseVariationsPanel: React.FC<ExerciseVariationsPanelProps> = ({
  open,
  exercise,
  variations,
  loading,
  onClose,
  onVariationsChange,
  onEditVariation,
  onError,
  onRefreshExercises,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [addVariationDialog, setAddVariationDialog] = useState(false);
  const [variationFormData, setVariationFormData] = useState<CreateVariationInput>({ variationName: '' });

  const handleAddVariation = async () => {
    if (!exercise || !variationFormData.variationName) return;
    try {
      await ExerciseService.createVariation(exercise.id, variationFormData);
      setAddVariationDialog(false);
      setVariationFormData({ variationName: '' });
      const data = await ExerciseService.getVariations(exercise.id);
      onVariationsChange(data.variations);
      onRefreshExercises();
    } catch (err) {
      onError('Failed to add variation');
      console.error(err);
    }
  };

  const handleDeleteVariation = async (variation: Exercise) => {
    if (!window.confirm(`Delete variation "${variation.variationName || variation.name}"?`)) return;
    try {
      await ExerciseService.delete(variation.id);
      onVariationsChange(variations.filter(v => v.id !== variation.id));
      onRefreshExercises();
    } catch (err) {
      onError('Failed to delete variation');
      console.error(err);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Вариации: {exercise?.name}</Typography>
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setAddVariationDialog(true)}>
              Добави
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Typography>Зареждане...</Typography>
          ) : variations.length === 0 ? (
            <Typography color="text.secondary">Няма вариации. Натиснете "Добави" за да създадете.</Typography>
          ) : (
            variations.map((v) => (
              <Box key={v.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid #eee' }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">{v.variationName || v.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{v.equipment} | {v.difficulty}</Typography>
                </Box>
                <Box>
                  <IconButton size="small" onClick={() => { onClose(); onEditVariation(v); }} sx={{ mr: 0.5 }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteVariation(v)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Затвори</Button>
        </DialogActions>
      </Dialog>

      {/* Add Variation Dialog */}
      <Dialog open={addVariationDialog} onClose={() => setAddVariationDialog(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Добави вариация на {exercise?.name}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Име на вариция (напр. Barbell, Dumbbell, Incline)"
            value={variationFormData.variationName}
            onChange={(e) => setVariationFormData({ ...variationFormData, variationName: e.target.value })}
            sx={{ mt: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Оборудване (незадължително)"
            value={variationFormData.equipment || ''}
            onChange={(e) => setVariationFormData({ ...variationFormData, equipment: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label="Инструкции (незадължително)"
            multiline
            rows={2}
            value={variationFormData.instructions || ''}
            onChange={(e) => setVariationFormData({ ...variationFormData, instructions: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddVariationDialog(false)}>Отказ</Button>
          <Button onClick={handleAddVariation} variant="contained" disabled={!variationFormData.variationName}>Добави</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExerciseVariationsPanel;
